/**
 * TenantSuspendedGate — render the suspended-tenant explanation INSTEAD of the
 * portal when the viewer's active organization is under an operator hold
 * (tenant-foundation Task 18 / Req 15.7).
 *
 * WHERE THIS SITS. A thin wrapper between the existing `ProtectedRoute` access
 * gate and the surfaces it protects, mirroring `OnboardingGate`. It deliberately
 * does NOT touch any `ProtectedRoute` guard: this is an additional explanatory
 * layer, never a replacement for an access check, and the route's own capability
 * requirements are unchanged.
 *
 * WHY IT RENDERS IN PLACE RATHER THAN REDIRECTING. A redirect needs a destination
 * that the viewer can actually render, and a suspended tenant has none — every
 * gated surface refuses. That is the same redirect-loop trap `AccessLockoutNotice`
 * was written to escape, so this follows its shape: render a stable, non-navigating
 * explanation.
 *
 * WHY IT IS NOT A SECURITY BOUNDARY. Suspension is enforced server-side — the
 * shared evaluator's `tenantConsentGate` denies on `tenantStatus: 'suspended'`, and
 * the backend resolver reads the Tenant row itself and fails closed when it cannot.
 * This gate only replaces a confusing blank app with an explanation. It therefore
 * fails OPEN by design: while memberships are still loading, or if the status
 * cannot be determined, it renders children and lets the server refuse. Blocking
 * the whole portal on a client-side guess would risk locking out a perfectly
 * active tenant over a failed query.
 */

import type { JSX, ReactNode } from 'react';

import { useAuth } from '@/hooks/useAuth';
import { useMyMemberships } from '@/hooks/useMyMemberships';

import { TenantStateNotice } from './TenantStateNotice';

export interface TenantSuspendedGateProps {
  /** The surface this gate protects, rendered whenever the tenant is not suspended. */
  children: ReactNode;
}

export function TenantSuspendedGate({ children }: TenantSuspendedGateProps): JSX.Element {
  const { user } = useAuth();
  const { memberships, status } = useMyMemberships();

  const activeTenantId = user?.activeTenantId?.trim();

  // Fail OPEN while unresolved (see the header): the server is the boundary.
  if (status !== 'resolved' || !activeTenantId) return <>{children}</>;

  const active = memberships.find((m) => m.tenantId === activeTenantId);
  if (!active || active.tenantStatus !== 'suspended') return <>{children}</>;

  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <TenantStateNotice
        kind="suspended"
        tenantName={active.tenantName}
        className="max-w-xl"
      />
    </div>
  );
}

export default TenantSuspendedGate;
